import { Decimal } from 'decimal.js'
import { Form } from 'factorial-form'
import includes from 'lodash/includes'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import { TIME_REGEX } from 'modules/core/lib/formatTime'

import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'
import { FIXED_LEAVE_IDENTIFIERS } from 'modules/timeoff/store/models/LeaveType'

class LeaveForm extends Form {
  constructor(values: {}) {
    super(values, {
      description: 'string',
      employee_id: 'number',
      employees_for_bulk_creation: 'any',
      finish_on: 'date',
      start_on: 'date',
      start_time: 'string',
      finish_time: 'string',
      hours_amount_in_cents: 'cents',
      half_day: 'string',
      leave_type_id: 'number',
      policy_id: 'number',
      // Medical leave fields
      medical_leave_type: 'string',
      medical_leave_document: 'file',
      effective_on: 'date',
      paternity_birth_on: 'date',
      accident_on: 'date',
      medical_discharge_reason: 'string',
      colegiate_number: 'string',
      has_previous_relapse: 'boolean',
      relapse_leave_id: 'number',
      relapse_on: 'date',
      delegated_approver_id: 'string',
      skip_eau_check: 'boolean',
    })
    makeObservable(this, {
      type: computed,
      medicalLeaveType: computed,
      isPaternity: computed,
      isAccident: computed,
      isSickness: computed,
    })
  }

  hasLeaveStarted() {
    const today = moment()
    const start = stringToDate(this.get('start_on').value, 'ui')

    return today.isSameOrAfter(start)
  }

  get type(): LeaveType | null | undefined {
    const field = this.get('leave_type_id')

    return leaveTypes.get(+field.value)
  }

  get isHourlyFormComplete(): boolean {
    const startTime = this.get('start_time').value
    const hoursAmount = this.get('hours_amount_in_cents').value
    const notZero = parseFloat(hoursAmount.replace(/,/, '.')) !== 0

    if (isNaN(hoursAmount)) return false
    return !!startTime.match(TIME_REGEX) && !!hoursAmount && notZero
  }

  get startTimeHasValue(): boolean {
    const startTime = this.get('start_time')
    return startTime.hasValue && TIME_REGEX.test(startTime.value)
  }

  get endDateOptional(): boolean {
    const type = this.getType()

    return !!type && type.attributes.allow_endless
  }

  getType(): LeaveType | null | undefined {
    const field = this.get('leave_type_id')

    return leaveTypes.get(+field.value)
  }

  acceptsAttachment(): boolean {
    const type = this.getType()

    return !!type && type.attributes.attachment
  }

  isSickType(): boolean {
    const type = this.getType()

    return !!type && type.attributes.identifier === FIXED_LEAVE_IDENTIFIERS.sick
  }

  isParentalType(): boolean {
    const type = this.getType()

    return !!type && type.attributes.identifier === FIXED_LEAVE_IDENTIFIERS.parental
  }

  isComputingNaturalDays(employeeTimeoffPolicyId?: number | null): boolean {
    const type = this.getType()
    const accrues = !!type && type.attributes.accrues

    const allowance = !!type && type.getAllowanceForEmployee(employeeTimeoffPolicyId)

    return accrues && !!allowance && allowance.natural
  }

  // Includes non working days at end of range
  isRegularCalendarDays(employeeTimeoffPolicyId?: number | null): boolean {
    const type = this.getType()

    const allowance = !!type && type.getAllowanceForEmployee(employeeTimeoffPolicyId)

    return !!allowance && allowance.regularCalendarDays
  }

  hoursAmount(): number | null {
    const hoursAmount = this.get('hours_amount_in_cents').value

    if (!hoursAmount) return null

    const value = new Decimal(hoursAmount).mul(100).toNumber()

    return value
  }

  // Medical Leave methods
  get medicalLeaveType(): string {
    return this.get('medical_leave_type').value
  }

  get isPaternity(): boolean {
    return Leave.medicalLeaveTypes.paternity === this.medicalLeaveType
  }

  get isAccident(): boolean {
    return includes(
      [Leave.medicalLeaveTypes.accident_working, Leave.medicalLeaveTypes.accident_other],
      this.medicalLeaveType
    )
  }

  get isSickness(): boolean {
    return includes(
      [Leave.medicalLeaveTypes.sickness_common, Leave.medicalLeaveTypes.sickness_professional],
      this.medicalLeaveType
    )
  }

  // Date helpers
  get startOn(): moment.Moment {
    return stringToDate(this.get('start_on').value, 'ui')
  }

  get finishOn(): moment.Moment {
    return stringToDate(this.get('finish_on').value, 'ui')
  }
}

export default LeaveForm
